import { useState } from "react";
import {
  CLASSES,
  ANCESTRIES,
  COMMUNITIES,
  LEVEL_1_DOMAIN_CARDS,
  TIER_1_PRIMARY_WEAPONS,
  TIER_1_SECONDARY_WEAPONS,
  TIER_1_ARMOR,
  STANDARD_TRAIT_MODIFIERS,
  STARTING_HOPE,
  STARTING_STRESS,
  STARTING_PROFICIENCY,
  type TraitName,
  type SrdClass,
  type SrdSubclass,
  type SrdAncestry,
  type SrdCommunity,
  type SrdWeapon,
  type SrdArmor,
  type SrdDomainCardLevel1,
} from "../shared/daggerheartSrd2.js";
import { DAGGERHEART_ART_MANIFEST } from "../shared/daggerheartArtManifest.js";

export interface CharacterCreatorState {
  name: string;
  pronouns: string;
  // Step 1: Class
  selectedClass: SrdClass | null;
  // Step 2: Subclass
  selectedSubclass: SrdSubclass | null;
  // Step 3: Ancestry
  isMixedAncestry: boolean;
  ancestry: SrdAncestry | null;
  mixedAncestryTop: SrdAncestry | null;
  mixedAncestryBottom: SrdAncestry | null;
  // Step 4: Community
  community: SrdCommunity | null;
  // Step 5: Traits
  traits: Record<TraitName, number | null>;
  // Step 6 & 7: Weapons
  primaryWeapon: SrdWeapon | null;
  secondaryWeapon: SrdWeapon | null;
  // Step 8: Armor
  armor: SrdArmor | null;
  // Step 9: Potion
  potionChoice: "Minor Health Potion" | "Minor Stamina Potion";
  // Step 10: Background
  backgroundText: string;
  // Step 11: Experiences
  experience1: string;
  experience2: string;
  // Step 12: Domain cards
  domainCards: SrdDomainCardLevel1[];
  // Step 13: Connections
  connectionsText: string;
}

const INITIAL_STATE: CharacterCreatorState = {
  name: "",
  pronouns: "",
  selectedClass: null,
  selectedSubclass: null,
  isMixedAncestry: false,
  ancestry: null,
  mixedAncestryTop: null,
  mixedAncestryBottom: null,
  community: null,
  traits: {
    Agility: null,
    Strength: null,
    Finesse: null,
    Instinct: null,
    Presence: null,
    Knowledge: null,
  },
  primaryWeapon: null,
  secondaryWeapon: null,
  armor: null,
  potionChoice: "Minor Health Potion",
  backgroundText: "",
  experience1: "",
  experience2: "",
  domainCards: [],
  connectionsText: "",
};

const TRAIT_NAMES: TraitName[] = [
  "Agility",
  "Strength",
  "Finesse",
  "Instinct",
  "Presence",
  "Knowledge",
];

const TOTAL_STEPS = 14;

function getCardImageUrl(id: string): string {
  const asset = DAGGERHEART_ART_MANIFEST[id];
  if (!asset) return "";
  return `/${asset.path.replace(/^public\//, "")}`;
}

export function CharacterCreatorWizard(props: {
  isOpen: boolean;
  onClose: () => void;
  playerName: string;
  onConfirmCharacter?: (character: CharacterCreatorState) => void;
}) {
  const { isOpen, onClose, playerName, onConfirmCharacter } = props;
  const [step, setStep] = useState<number>(1);
  const [state, setState] = useState<CharacterCreatorState>({
    ...INITIAL_STATE,
    name: playerName || "",
  });
  const [confirmed, setConfirmed] = useState(false);

  if (!isOpen) return null;

  const currentClass = state.selectedClass;

  // Compute unassigned trait modifiers pool
  const computeUnassignedModifiers = (): number[] => {
    const pool: number[] = [...STANDARD_TRAIT_MODIFIERS];
    for (const trait of TRAIT_NAMES) {
      const val = state.traits[trait];
      if (val !== null) {
        const idx = pool.indexOf(val);
        if (idx >= 0) pool.splice(idx, 1);
      }
    }
    return pool;
  };

  const getAvailableModifiersForTrait = (trait: TraitName): number[] => {
    const pool: number[] = [...STANDARD_TRAIT_MODIFIERS];
    for (const other of TRAIT_NAMES) {
      if (other === trait) continue;
      const val = state.traits[other];
      if (val !== null) {
        const idx = pool.indexOf(val);
        if (idx >= 0) pool.splice(idx, 1);
      }
    }
    // Return sorted descending unique values for the dropdown
    return Array.from(new Set(pool)).sort((a, b) => b - a);
  };

  const unassignedPool = computeUnassignedModifiers();

  // Step validation and helpful failure reason
  const getStepStatus = (): { ok: boolean; reason: string } => {
    switch (step) {
      case 1:
        return Boolean(state.selectedClass && state.selectedClass.name)
          ? { ok: true, reason: "" }
          : { ok: false, reason: "Please choose a Class to continue." };
      case 2:
        return state.selectedSubclass !== null
          ? { ok: true, reason: "" }
          : { ok: false, reason: "Please choose a Subclass to continue." };
      case 3:
        if (state.isMixedAncestry) {
          if (!state.mixedAncestryTop || !state.mixedAncestryBottom) {
            return {
              ok: false,
              reason: "Please choose both a Top and Bottom feature Ancestry.",
            };
          }
          return { ok: true, reason: "" };
        }
        return state.ancestry !== null
          ? { ok: true, reason: "" }
          : { ok: false, reason: "Please choose an Ancestry to continue." };
      case 4:
        return state.community !== null
          ? { ok: true, reason: "" }
          : { ok: false, reason: "Please choose a Community to continue." };
      case 5: {
        const vals = Object.values(state.traits);
        if (vals.some((v) => v === null)) {
          return {
            ok: false,
            reason: `Assign all 6 traits. Remaining: [${unassignedPool.map((n) => (n >= 0 ? `+${n}` : n)).join(", ")}]`,
          };
        }
        return { ok: true, reason: "" };
      }
      case 6:
        return state.primaryWeapon !== null
          ? { ok: true, reason: "" }
          : { ok: false, reason: "Please choose a Primary Weapon to continue." };
      case 7:
        // Secondary weapon is required only if dual-wielding, but user can always proceed
        return { ok: true, reason: "" };
      case 8:
        return state.armor !== null
          ? { ok: true, reason: "" }
          : { ok: false, reason: "Please choose Armor to continue." };
      case 9:
        return state.potionChoice !== null
          ? { ok: true, reason: "" }
          : { ok: false, reason: "Please choose a Starting Potion to continue." };
      case 10:
        return state.name.trim().length > 0
          ? { ok: true, reason: "" }
          : { ok: false, reason: "Please enter a character name to continue." };
      case 11:
        if (!state.experience1.trim() || !state.experience2.trim()) {
          return {
            ok: false,
            reason: "Please enter both Experiences (+2 each) to continue.",
          };
        }
        return { ok: true, reason: "" };
      case 12:
        if (state.domainCards.length !== 2) {
          return {
            ok: false,
            reason: `Please choose exactly 2 Level 1 domain cards (${state.domainCards.length}/2 selected).`,
          };
        }
        return { ok: true, reason: "" };
      case 13:
        return { ok: true, reason: "" };
      case 14:
        if (!state.selectedClass || !state.selectedClass.name) {
          return { ok: false, reason: "Please choose a Class before confirming." };
        }
        if (!state.name.trim()) {
          return { ok: false, reason: "Character name is required before confirming." };
        }
        return { ok: true, reason: "" };
      default:
        return { ok: true, reason: "" };
    }
  };

  const stepStatus = getStepStatus();

  const handleNext = () => {
    if (!stepStatus.ok) return;
    if (step === 6 && state.primaryWeapon?.burden === "Two-Handed") {
      // Two-handed primary weapon skips secondary weapon step (goes to armor)
      setStep(8);
      return;
    }
    if (step < TOTAL_STEPS) setStep((s) => s + 1);
  };

  const handleBack = () => {
    if (step === 8 && state.primaryWeapon?.burden === "Two-Handed") {
      // Return to primary weapon
      setStep(6);
      return;
    }
    if (step > 1) setStep((s) => s - 1);
  };

  const handleTraitAssign = (trait: TraitName, val: number | null) => {
    setState((prev) => ({
      ...prev,
      traits: {
        ...prev.traits,
        [trait]: val,
      },
    }));
  };

  const toggleDomainCard = (card: SrdDomainCardLevel1) => {
    setState((prev) => {
      const exists = prev.domainCards.some((c) => c.name === card.name);
      if (exists) {
        return {
          ...prev,
          domainCards: prev.domainCards.filter((c) => c.name !== card.name),
        };
      }
      if (prev.domainCards.length >= 2) return prev;
      return {
        ...prev,
        domainCards: [...prev.domainCards, card],
      };
    });
  };

  const handleConfirm = () => {
    setConfirmed(true);
    if (onConfirmCharacter) {
      onConfirmCharacter(state);
    }
  };

  const stepTitles: Record<number, string> = {
    1: "Step 1: Choose Class",
    2: "Step 2: Choose Subclass",
    3: "Step 3: Choose Ancestry",
    4: "Step 4: Choose Community",
    5: "Step 5: Assign Traits",
    6: "Step 6: Choose Primary Weapon",
    7: "Step 7: Choose Secondary Weapon (Optional)",
    8: "Step 8: Choose Armor",
    9: "Step 9: Choose Starting Potion",
    10: "Step 10: Character Background",
    11: "Step 11: Enter Experiences (+2)",
    12: "Step 12: Level 1 Domain Cards (Pick 2)",
    13: "Step 13: Connections Note",
    14: "Step 14: Review & Confirm",
  };

  return (
    <div className="cc-modal-backdrop" onClick={onClose}>
      <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cc-header">
          <div>
            <h3>Character Creator (Daggerheart SRD 2.0)</h3>
            <span className="cc-step-indicator">
              Page {step} of {TOTAL_STEPS}: {stepTitles[step]}
            </span>
          </div>
          <button
            type="button"
            className="btn secondary btn-tiny"
            onClick={onClose}
            title="Close wizard"
          >
            ×
          </button>
        </div>

        <div className="cc-body">
          {/* STEP 1: Class */}
          {step === 1 && (
            <div className="cc-step-content">
              <h4>Choose a Class (1 of 13)</h4>
              <p className="cc-hint">
                Select your class archetype to determine your two domains, evasion, starting hit points, and class features.
              </p>
              <div className="cc-card-grid">
                {Object.values(CLASSES).map((cls) => {
                  const isSelected = state.selectedClass?.name === cls.name;
                  const imgUrl = getCardImageUrl(`class-${cls.name.toLowerCase()}`);
                  return (
                    <div
                      key={cls.name}
                      className={`cc-select-card ${isSelected ? "is-selected" : ""}`}
                      onClick={() =>
                        setState((prev) => ({
                          ...prev,
                          selectedClass: cls,
                          selectedSubclass: null,
                          domainCards: [],
                        }))
                      }
                    >
                      {imgUrl && <img src={imgUrl} alt={cls.name} className="cc-card-thumb" />}
                      <div className="cc-card-info">
                        <strong>{cls.name}</strong>
                        <span className="cc-badge">
                          {cls.domains[0]} &amp; {cls.domains[1]}
                        </span>
                        <p className="cc-card-meta">
                          Evasion: {cls.startingEvasion} · HP: {cls.startingHp}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 2: Subclass */}
          {step === 2 && (
            <div className="cc-step-content">
              <h4>Choose a Subclass for {currentClass?.name}</h4>
              <p className="cc-hint">
                Each class has two distinct subclasses granting unique starting Foundation features.
              </p>
              <div className="cc-card-grid">
                {currentClass?.subclasses.map((sc) => {
                  const isSelected = state.selectedSubclass?.name === sc.name;
                  const scSlug = sc.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                  const imgUrl = getCardImageUrl(
                    `subclass-${currentClass.name.toLowerCase()}-${scSlug}`,
                  );
                  return (
                    <div
                      key={sc.name}
                      className={`cc-select-card ${isSelected ? "is-selected" : ""}`}
                      onClick={() =>
                        setState((prev) => ({
                          ...prev,
                          selectedSubclass: sc,
                        }))
                      }
                    >
                      {imgUrl && <img src={imgUrl} alt={sc.name} className="cc-card-thumb" />}
                      <div className="cc-card-info">
                        <strong>{sc.name}</strong>
                        {sc.spellcastTrait && (
                          <span className="cc-badge">Spellcast: {sc.spellcastTrait}</span>
                        )}
                        <p className="cc-desc">{sc.foundation.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 3: Ancestry */}
          {step === 3 && (
            <div className="cc-step-content">
              <div className="cc-row-toggle">
                <h4>Choose Ancestry</h4>
                <label className="cc-checkbox-label">
                  <input
                    type="checkbox"
                    checked={state.isMixedAncestry}
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        isMixedAncestry: e.target.checked,
                      }))
                    }
                  />
                  Mixed Ancestry (Top feature of one + Bottom feature of another)
                </label>
              </div>

              {!state.isMixedAncestry ? (
                <div className="cc-card-grid">
                  {Object.values(ANCESTRIES).map((anc) => {
                    const isSelected = state.ancestry?.name === anc.name;
                    const imgUrl = getCardImageUrl(`ancestry-${anc.name.toLowerCase()}`);
                    return (
                      <div
                        key={anc.name}
                        className={`cc-select-card ${isSelected ? "is-selected" : ""}`}
                        onClick={() => setState((prev) => ({ ...prev, ancestry: anc }))}
                      >
                        {imgUrl && <img src={imgUrl} alt={anc.name} className="cc-card-thumb" />}
                        <div className="cc-card-info">
                          <strong>{anc.name}</strong>
                          <p className="cc-desc">
                            <b>{anc.feature1.name}:</b> {anc.feature1.text}
                          </p>
                          <p className="cc-desc">
                            <b>{anc.feature2.name}:</b> {anc.feature2.text}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="cc-mixed-container">
                  <div className="cc-mixed-col">
                    <h5>Top Feature Ancestry</h5>
                    <div className="cc-card-grid-compact">
                      {Object.values(ANCESTRIES).map((anc) => {
                        const imgUrl = getCardImageUrl(`ancestry-${anc.name.toLowerCase()}`);
                        return (
                          <div
                            key={`top-${anc.name}`}
                            className={`cc-select-card-mini ${
                              state.mixedAncestryTop?.name === anc.name ? "is-selected" : ""
                            }`}
                            onClick={() =>
                              setState((prev) => ({ ...prev, mixedAncestryTop: anc }))
                            }
                          >
                            {imgUrl && <img src={imgUrl} alt={anc.name} className="cc-card-thumb-sm" />}
                            <strong>{anc.name}</strong>
                            <p className="cc-desc">
                              <b>{anc.feature1.name}:</b> {anc.feature1.text}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="cc-mixed-col">
                    <h5>Bottom Feature Ancestry</h5>
                    <div className="cc-card-grid-compact">
                      {Object.values(ANCESTRIES).map((anc) => {
                        const imgUrl = getCardImageUrl(`ancestry-${anc.name.toLowerCase()}`);
                        return (
                          <div
                            key={`bot-${anc.name}`}
                            className={`cc-select-card-mini ${
                              state.mixedAncestryBottom?.name === anc.name ? "is-selected" : ""
                            }`}
                            onClick={() =>
                              setState((prev) => ({ ...prev, mixedAncestryBottom: anc }))
                            }
                          >
                            {imgUrl && <img src={imgUrl} alt={anc.name} className="cc-card-thumb-sm" />}
                            <strong>{anc.name}</strong>
                            <p className="cc-desc">
                              <b>{anc.feature2.name}:</b> {anc.feature2.text}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 4: Community */}
          {step === 4 && (
            <div className="cc-step-content">
              <h4>Choose Community (1 of 15)</h4>
              <p className="cc-hint">
                Represents your character's culture or environment of origin.
              </p>
              <div className="cc-card-grid">
                {Object.values(COMMUNITIES).map((com) => {
                  const isSelected = state.community?.name === com.name;
                  const imgUrl = getCardImageUrl(`community-${com.name.toLowerCase()}`);
                  return (
                    <div
                      key={com.name}
                      className={`cc-select-card ${isSelected ? "is-selected" : ""}`}
                      onClick={() => setState((prev) => ({ ...prev, community: com }))}
                    >
                      {imgUrl && <img src={imgUrl} alt={com.name} className="cc-card-thumb" />}
                      <div className="cc-card-info">
                        <strong>{com.name}</strong>
                        <p className="cc-desc">
                          <b>{com.feature.name}:</b> {com.feature.text}
                        </p>
                        <span className="cc-meta">{com.adjectives.slice(0, 4).join(", ")}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 5: Traits */}
          {step === 5 && (
            <div className="cc-step-content">
              <h4>Assign Character Traits</h4>
              <p className="cc-hint">
                Standard array modifiers: <strong>+2, +1, +1, +0, +0, -1</strong>.
              </p>

              {/* Dynamic pool status display */}
              <div className="cc-modifier-pool-bar">
                <span className="cc-pool-title">Unassigned Modifiers:</span>
                {unassignedPool.length === 0 ? (
                  <span className="cc-pool-tag is-complete">✓ All 6 modifiers assigned!</span>
                ) : (
                  unassignedPool.map((mod, i) => (
                    <span key={i} className="cc-pool-tag">
                      {mod >= 0 ? `+${mod}` : mod}
                    </span>
                  ))
                )}
              </div>

              <div className="cc-traits-grid">
                {TRAIT_NAMES.map((trait) => {
                  const currentVal = state.traits[trait];
                  const availableForThisTrait = getAvailableModifiersForTrait(trait);
                  return (
                    <div key={trait} className="cc-trait-row">
                      <span className="cc-trait-name">{trait}</span>
                      <select
                        className="cc-select"
                        value={currentVal !== null ? String(currentVal) : ""}
                        onChange={(e) => {
                          const val = e.target.value === "" ? null : parseInt(e.target.value, 10);
                          handleTraitAssign(trait, val);
                        }}
                      >
                        <option value="">-- Unassigned --</option>
                        {/* Always include currently selected value so it stays visible */}
                        {currentVal !== null && !availableForThisTrait.includes(currentVal) && (
                          <option value={String(currentVal)}>
                            {currentVal >= 0 ? `+${currentVal}` : currentVal} (current)
                          </option>
                        )}
                        {/* Only show available unassigned modifiers */}
                        {availableForThisTrait.map((m, idx) => (
                          <option key={idx} value={String(m)}>
                            {m >= 0 ? `+${m}` : m}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>

              <div className="cc-auto-stats">
                <h5>Auto-Calculated Level 1 Statistics:</h5>
                <div className="cc-stats-chips">
                  <span>Level: 1</span>
                  <span>Proficiency: {STARTING_PROFICIENCY}</span>
                  <span>Starting Hope: {STARTING_HOPE}</span>
                  <span>Starting Stress: {STARTING_STRESS}</span>
                  <span>Starting HP: {state.selectedClass?.startingHp ?? 6}</span>
                  <span>Starting Evasion: {state.selectedClass?.startingEvasion ?? 10}</span>
                </div>
              </div>
            </div>
          )}

          {/* STEP 6: Primary Weapon */}
          {step === 6 && (
            <div className="cc-step-content">
              <h4>Choose Primary Weapon (Tier 1)</h4>
              <p className="cc-hint">
                Select your primary weapon. Two-handed weapons fill both hands; one-handed weapons allow choosing an offhand weapon next.
              </p>
              <div className="cc-equip-grid">
                {TIER_1_PRIMARY_WEAPONS.map((w) => (
                  <div
                    key={w.name}
                    className={`cc-select-card-mini ${
                      state.primaryWeapon?.name === w.name ? "is-selected" : ""
                    }`}
                    onClick={() => setState((prev) => ({ ...prev, primaryWeapon: w }))}
                  >
                    <img
                      src={getCardImageUrl("weapon-tier-1-generic")}
                      alt="Weapon"
                      className="cc-card-thumb-sm"
                    />
                    <strong>{w.name}</strong>
                    <span className="cc-badge">{w.burden}</span>
                    <span>
                      {w.trait} · {w.range} · {w.damage} {w.damageType}
                    </span>
                    {w.feature && <p className="cc-desc">{w.feature}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 7: Secondary Weapon (Optional) */}
          {step === 7 && (
            <div className="cc-step-content">
              <h4>Choose Secondary Weapon (Optional)</h4>
              <p className="cc-hint">
                Your primary weapon ({state.primaryWeapon?.name}) is One-Handed. You can choose a secondary weapon or shield to equip in your offhand, or skip to armor.
              </p>
              <div className="cc-equip-grid">
                <div
                  className={`cc-select-card-mini ${state.secondaryWeapon === null ? "is-selected" : ""}`}
                  onClick={() => setState((prev) => ({ ...prev, secondaryWeapon: null }))}
                >
                  <strong>None (Single Weapon)</strong>
                  <span>Leave offhand free</span>
                </div>
                {TIER_1_SECONDARY_WEAPONS.map((w) => (
                  <div
                    key={w.name}
                    className={`cc-select-card-mini ${
                      state.secondaryWeapon?.name === w.name ? "is-selected" : ""
                    }`}
                    onClick={() => setState((prev) => ({ ...prev, secondaryWeapon: w }))}
                  >
                    <img
                      src={getCardImageUrl("weapon-tier-1-generic")}
                      alt="Secondary Weapon"
                      className="cc-card-thumb-sm"
                    />
                    <strong>{w.name}</strong>
                    <span>
                      {w.trait} · {w.range} · {w.damage} {w.damageType}
                    </span>
                    {w.feature && <p className="cc-desc">{w.feature}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 8: Armor */}
          {step === 8 && (
            <div className="cc-step-content">
              <h4>Choose Armor (Tier 1)</h4>
              <p className="cc-hint">Select one set of armor from the Tier 1 Armor Table.</p>
              <div className="cc-equip-grid">
                {TIER_1_ARMOR.map((a) => (
                  <div
                    key={a.name}
                    className={`cc-select-card-mini ${state.armor?.name === a.name ? "is-selected" : ""}`}
                    onClick={() => setState((prev) => ({ ...prev, armor: a }))}
                  >
                    <img
                      src={getCardImageUrl("armor-tier-1-generic")}
                      alt="Armor"
                      className="cc-card-thumb-sm"
                    />
                    <strong>{a.name}</strong>
                    <span>
                      Score: {a.baseScore} · Thresholds: {a.baseMajor}/{a.baseSevere}
                    </span>
                    {a.feature && <p className="cc-desc">{a.feature}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 9: Starting Potion */}
          {step === 9 && (
            <div className="cc-step-content">
              <h4>Choose Starting Potion</h4>
              <p className="cc-hint">
                Every character receives basic supplies (torch, 50ft rope, basic supplies, 1 handful of gold) plus their choice of a starting potion.
              </p>
              <div className="cc-card-grid">
                <div
                  className={`cc-select-card ${
                    state.potionChoice === "Minor Health Potion" ? "is-selected" : ""
                  }`}
                  onClick={() => setState((prev) => ({ ...prev, potionChoice: "Minor Health Potion" }))}
                >
                  <img
                    src={getCardImageUrl("item-minor-health-potion")}
                    alt="Minor Health Potion"
                    className="cc-card-thumb"
                  />
                  <strong>Minor Health Potion</strong>
                  <p className="cc-desc">Clear 1d4 Hit Points when consumed.</p>
                </div>
                <div
                  className={`cc-select-card ${
                    state.potionChoice === "Minor Stamina Potion" ? "is-selected" : ""
                  }`}
                  onClick={() => setState((prev) => ({ ...prev, potionChoice: "Minor Stamina Potion" }))}
                >
                  <img
                    src={getCardImageUrl("item-minor-stamina-potion")}
                    alt="Minor Stamina Potion"
                    className="cc-card-thumb"
                  />
                  <strong>Minor Stamina Potion</strong>
                  <p className="cc-desc">Clear 1d4 Stress when consumed.</p>
                </div>
              </div>
            </div>
          )}

          {/* STEP 10: Character Details & Background */}
          {step === 10 && (
            <div className="cc-step-content">
              <h4>Character Details & Background</h4>
              <p className="cc-hint">
                Enter your character name (required) and background details (optional).
              </p>
              <div className="cc-form-group" style={{ marginBottom: "0.85rem" }}>
                <label style={{ display: "block", fontWeight: 700, marginBottom: "0.25rem" }}>
                  Character Name (required)
                </label>
                <input
                  type="text"
                  className="cc-input"
                  placeholder="e.g. Rell, Osha, Jory"
                  value={state.name}
                  onChange={(e) =>
                    setState((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="cc-form-group" style={{ marginBottom: "0.85rem" }}>
                <label style={{ display: "block", fontWeight: 700, marginBottom: "0.25rem" }}>
                  Pronouns (optional)
                </label>
                <input
                  type="text"
                  className="cc-input"
                  placeholder="e.g. they/them, she/her, he/him"
                  value={state.pronouns}
                  onChange={(e) =>
                    setState((prev) => ({ ...prev, pronouns: e.target.value }))
                  }
                />
              </div>
              <div className="cc-form-group">
                <label style={{ display: "block", fontWeight: 700, marginBottom: "0.25rem" }}>
                  Background Story (optional)
                </label>
                <textarea
                  className="cc-textarea"
                  rows={5}
                  value={state.backgroundText}
                  onChange={(e) =>
                    setState((prev) => ({ ...prev, backgroundText: e.target.value }))
                  }
                  placeholder="Where did you grow up? What key event drove you to adventure? Who was your mentor, family, or rival?"
                />
              </div>
            </div>
          )}

          {/* STEP 11: Experiences */}
          {step === 11 && (
            <div className="cc-step-content">
              <h4>Enter Two Experiences (+2 each)</h4>
              <p className="cc-hint">
                Words or short phrases encapsulating past skills, traits, or training. Each starts with a +2 modifier.
              </p>
              <div className="cc-form-group">
                <label>Experience 1 (+2)</label>
                <input
                  type="text"
                  className="cc-input"
                  placeholder="e.g. Wilderness Guide, Veteran Mercenary, Pickpocket"
                  value={state.experience1}
                  onChange={(e) =>
                    setState((prev) => ({ ...prev, experience1: e.target.value }))
                  }
                />
              </div>
              <div className="cc-form-group">
                <label>Experience 2 (+2)</label>
                <input
                  type="text"
                  className="cc-input"
                  placeholder="e.g. Stealth, Ancient Lore, Street Smart"
                  value={state.experience2}
                  onChange={(e) =>
                    setState((prev) => ({ ...prev, experience2: e.target.value }))
                  }
                />
              </div>
            </div>
          )}

          {/* STEP 12: Level 1 Domain Cards */}
          {step === 12 && (
            <div className="cc-step-content">
              <h4>
                Level 1 Domain Cards for {currentClass?.name} (Pick 2)
              </h4>
              <p className="cc-hint">
                Available domains: <strong>{currentClass?.domains.join(" & ")}</strong>. Selected: {state.domainCards.length} / 2.
              </p>
              <div className="cc-card-grid">
                {LEVEL_1_DOMAIN_CARDS.filter((c) =>
                  currentClass ? currentClass.domains.includes(c.domain) : true,
                ).map((card) => {
                  const isPicked = state.domainCards.some((c) => c.name === card.name);
                  const slug = card.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                  const imgUrl = getCardImageUrl(`card-${card.domain.toLowerCase()}-${slug}`);
                  return (
                    <div
                      key={card.name}
                      className={`cc-select-card ${isPicked ? "is-selected" : ""}`}
                      onClick={() => toggleDomainCard(card)}
                    >
                      {imgUrl && <img src={imgUrl} alt={card.name} className="cc-card-thumb" />}
                      <div className="cc-card-info">
                        <strong>{card.name}</strong>
                        <span className="cc-badge">
                          {card.domain} · {card.type} · Recall: {card.recallCost}
                        </span>
                        <p className="cc-desc">{card.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 13: Connections */}
          {step === 13 && (
            <div className="cc-step-content">
              <h4>Connections Note</h4>
              <p className="cc-hint">
                Note your relationships or bonds with fellow adventurers at the table.
              </p>
              <textarea
                className="cc-textarea"
                rows={6}
                value={state.connectionsText}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, connectionsText: e.target.value }))
                }
                placeholder="What favor did you promise another PC? What secret do you share?"
              />
            </div>
          )}

          {/* STEP 14: Review & Confirm */}
          {step === 14 && (
            <div className="cc-step-content">
              <h4>Review Character</h4>
              {confirmed ? (
                <div className="cc-confirm-box">
                  <h5>✓ Character Created on the Board!</h5>
                  <p>
                    Your token for <strong>{state.name || state.selectedClass?.name}</strong> has been created with its class HP, portrait, and stat block note.
                  </p>
                  <p>
                    Five cards have been placed into your private hand: your Ancestry card, Community card, Subclass Foundation card, and your 2 chosen Level 1 Domain cards.
                  </p>
                </div>
              ) : (
                <div className="cc-summary-box">
                  <p>
                    <strong>Class:</strong> {state.selectedClass?.name} (
                    {state.selectedSubclass?.name})
                  </p>
                  <p>
                    <strong>Heritage:</strong>{" "}
                    {state.isMixedAncestry
                      ? `Mixed: ${state.mixedAncestryTop?.name} / ${state.mixedAncestryBottom?.name}`
                      : state.ancestry?.name}{" "}
                    · {state.community?.name}
                  </p>
                  <p>
                    <strong>Traits:</strong> Agi: {state.traits.Agility ?? 0}, Str:{" "}
                    {state.traits.Strength ?? 0}, Fin: {state.traits.Finesse ?? 0}, Ins:{" "}
                    {state.traits.Instinct ?? 0}, Pre: {state.traits.Presence ?? 0}, Kno:{" "}
                    {state.traits.Knowledge ?? 0}
                  </p>
                  <p>
                    <strong>Stats:</strong> Evasion: {state.selectedClass?.startingEvasion}, HP:{" "}
                    {state.selectedClass?.startingHp}, Hope: {STARTING_HOPE}, Stress: {STARTING_STRESS}, Proficiency: {STARTING_PROFICIENCY}
                  </p>
                  <p>
                    <strong>Primary Weapon:</strong> {state.primaryWeapon?.name} (
                    {state.primaryWeapon?.damage} {state.primaryWeapon?.damageType},{" "}
                    {state.primaryWeapon?.range})
                  </p>
                  {state.secondaryWeapon && (
                    <p>
                      <strong>Secondary Weapon:</strong> {state.secondaryWeapon?.name} (
                      {state.secondaryWeapon?.damage} {state.secondaryWeapon?.damageType},{" "}
                      {state.secondaryWeapon?.range})
                    </p>
                  )}
                  <p>
                    <strong>Armor:</strong> {state.armor?.name} (Score: {state.armor?.baseScore}, Thresholds: {state.armor?.baseMajor}/{state.armor?.baseSevere})
                  </p>
                  <p>
                    <strong>Potion:</strong> {state.potionChoice}
                  </p>
                  <p>
                    <strong>Experiences (+2):</strong> {state.experience1 || "None"} &amp;{" "}
                    {state.experience2 || "None"}
                  </p>
                  <p>
                    <strong>Domain Cards:</strong>{" "}
                    {state.domainCards.map((c) => `${c.name} [${c.domain}]`).join(", ")}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="cc-footer">
          <button
            type="button"
            className="btn secondary btn-tiny"
            onClick={handleBack}
            disabled={step === 1}
          >
            ← Back
          </button>

          {!stepStatus.ok && (
            <span className="cc-footer-hint" title={stepStatus.reason}>
              {stepStatus.reason}
            </span>
          )}

          <div className="cc-footer-right">
            <button type="button" className="btn secondary btn-tiny" onClick={onClose}>
              Cancel
            </button>
            {step < TOTAL_STEPS ? (
              <button
                type="button"
                className="btn primary btn-tiny"
                onClick={handleNext}
                disabled={!stepStatus.ok}
              >
                Next →
              </button>
            ) : (
              <button
                type="button"
                className="btn primary btn-tiny mod-cta"
                onClick={handleConfirm}
                disabled={confirmed || !stepStatus.ok || !state.name.trim() || !state.selectedClass}
              >
                {confirmed ? "Character Created" : "Confirm Character"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
